import React, { useState, useCallback } from "react";

import FormField from "../../../../components/form-field/FormField";
import { NameEntity } from "../../../../helpers/NameEntity";
import SuggestionBox from "../../../../components/suggestion-box/SuggestionBox";
import { UrlParams } from "../../../../components/datatable/interfaces";
import { RequiredValidatorModel } from "../../../../services/form/validators/required/RequiredValidator";
import { RequiredObjectValidatorModel } from "../../../../services/form/validators/required/RequiredObjectValidator";
import { UsersStorageModel } from "../../../../services/user/UsersStorage";
import { OrganizationContactDetails } from "../../../../services/organization/Organization";
import { api } from "../../../../services/api/ApiService";
import { translationService } from "../../../../services/translation/TranslationService";

interface DisplayOrganizationNameProps {
  name: string;
}

export function DisplayOrganizationName(props: DisplayOrganizationNameProps) {
  return (
    <FormField
      id="organization-name"
      labelKey="organization.DisplayOrganizationName"
      readOnly
      fieldValue={props.name}
    />
  );
}

export function EditOrganizationName(props: DisplayOrganizationNameProps) {
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));

  return (
    <FormField
      id="organization-name"
      className="input"
      labelKey="organization.DisplayOrganizationName"
      required
      component="input"
      fieldName="name"
      fieldValue={props.name}
      validator={requiredValidator}
      placeholderKey="organization.EditOrganizationName.placeholder"
      maxLength={100}
      errorName="name"
    />
  );
}

interface DisplayOrganizationCountryProps {
  code: string;
}

export function DisplayOrganizationCountry(props: DisplayOrganizationCountryProps) {
  return (
    <FormField
      id="organization-country"
      labelKey="organization.DisplayOrganizationCountry"
      readOnly
      fieldValue={translationService.translate(`general.country.${props.code}`)}
    />
  );
}

interface DisplayOrganizationStateProps {
  state: string;
}

export function DisplayOrganizationState(props: DisplayOrganizationStateProps) {
  return (
    <FormField
      id="organization-state"
      labelKey="organization.DisplayOrganizationState"
      readOnly
      fieldValue={props.state || "-"}
    />
  );
}

interface DisplayOrganizationCityProps {
  city: string;
}

export function DisplayOrganizationCity(props: DisplayOrganizationCityProps) {
  return (
    <FormField
      id="organization-city"
      labelKey="organization.DisplayOrganizationCity"
      readOnly
      fieldValue={props.city || "-"}
    />
  );
}

interface DisplayOrganizationZipProps {
  zip: string;
}

export function DisplayOrganizationZip(props: DisplayOrganizationZipProps) {
  return (
    <FormField
      id="organization-zip"
      labelKey="organization.DisplayOrganizationZip"
      readOnly
      fieldValue={props.zip || "-"}
    />
  );
}

interface DisplayOrganizationAddressProps {
  address: string;
}

export function DisplayOrganizationAddress(props: DisplayOrganizationAddressProps) {
  return (
    <FormField
      id="organization-address"
      labelKey="organization.DisplayOrganizationAddress"
      readOnly
      fieldValue={props.address || "-"}
    />
  );
}

interface DisplayOrganizationHouseNumberProps {
  number: string;
}

export function DisplayOrganizationHouseNumber(props: DisplayOrganizationHouseNumberProps) {
  return (
    <FormField
      id="organization-address-number"
      labelKey="organization.DisplayOrganizationAddressNumber"
      readOnly
      fieldValue={props.number || "-"}
    />
  );
}

interface DisplayOrganizationVatProps {
  vat: string;
}

export function DisplayOrganizationVat(props: DisplayOrganizationVatProps) {
  return (
    <FormField
      id="organization-vat"
      labelKey="organization.DisplayOrganizationVat"
      readOnly
      fieldValue={props.vat || "-"}
    />
  );
}
interface DisplayOrganizationContactNameProps {
  contactName: string;
}

export function DisplayOrganizationContactName(props: DisplayOrganizationContactNameProps) {
  return (
    <FormField
      id="organization-contact-name"
      labelKey="organization.DisplayOrganizationContactName"
      readOnly
      fieldValue={props.contactName}
    />
  );
}

const mapOrganizationContactNameToUIObject = (
  organizationContactDetails: OrganizationContactDetails[]
) => {
  return organizationContactDetails.map((c) => {
    return { id: c.userId, name: c.name, key: c.userId };
  });
};
const mapOrganizationContactEmailToUIObject = (
  organizationContactDetails: OrganizationContactDetails[]
) => {
  return organizationContactDetails.map((c) => {
    return { id: c.userId, name: c.email, key: c.userId };
  });
};

interface EditOrganizationContactDetailsProps {
  contactUserName: NameEntity;
  contactUserEmail: NameEntity;
  setFieldValue: Function;
}

export function EditOrganizationContactDetails(props: EditOrganizationContactDetailsProps) {
  const [requiredObjectValidator] = useState(() => RequiredObjectValidatorModel.create({}));

  const { setFieldValue } = props;
  const [searchedOrganizationContactDetailsResult, setSearchedOrganizationContactDetailsResult]: [
    OrganizationContactDetails[],
    Function
  ] = useState([]);

  const [usersService] = useState(() => UsersStorageModel.create({}, { api }));

  const handleSearchContactNameChange = useCallback(
    async (searchText: string) => {
      const contactNameQueryParam: UrlParams = { name: searchText };
      const organizationContactDetails = await usersService.getAdminFilteredUsers(
        contactNameQueryParam
      );
      setSearchedOrganizationContactDetailsResult(organizationContactDetails);
      return mapOrganizationContactNameToUIObject(organizationContactDetails);
    },
    [usersService]
  );

  const handleSearchContactEmailChange = useCallback(
    async (searchText: string) => {
      const contactEmailQueryParam: UrlParams = { email: searchText };
      const organizationContactDetails = await usersService.getAdminFilteredUsers(
        contactEmailQueryParam
      );

      setSearchedOrganizationContactDetailsResult(organizationContactDetails);
      return mapOrganizationContactEmailToUIObject(organizationContactDetails);
    },
    [usersService]
  );

  const changeOrganizationContactName = useCallback(
    (organizationContactDetailsName: NameEntity | undefined) => {
      const id: number | null = organizationContactDetailsName
        ? (organizationContactDetailsName.id as number)
        : null;
      const name = organizationContactDetailsName ? organizationContactDetailsName.name : null;
      setFieldValue("contactUserId", id);
      setFieldValue("contactUserName", { id, name });
      setFieldValue("contactUserEmail", {
        id,
        name: searchedOrganizationContactDetailsResult.find((x) => x.userId === id)?.email,
      });
    },
    [searchedOrganizationContactDetailsResult, setFieldValue]
  );

  const changeOrganizationContactEmail = useCallback(
    (organizationContactDetailsEmail: NameEntity | undefined) => {
      const id: number | null = organizationContactDetailsEmail
        ? (organizationContactDetailsEmail.id as number)
        : null;
      const name = organizationContactDetailsEmail ? organizationContactDetailsEmail.name : null;
      setFieldValue("contactUserId", id);
      setFieldValue("contactUserName", {
        id,
        name: searchedOrganizationContactDetailsResult.find((x) => x.userId === id)?.name,
      });
      setFieldValue("contactUserEmail", { id: id, name });
    },
    [searchedOrganizationContactDetailsResult, setFieldValue]
  );

  const clearContactNameFieldValue = (text: string) => {
    if (!text) setFieldValue("contactUserName", "");
  };

  const clearEmailFieldValue = (text: string) => {
    if (!text) setFieldValue("contactUserEmail", "");
  };

  return (
    <div className="organization-contact-details-edit">
      <FormField
        id="contact-user-name"
        labelKey="organization.DisplayOrganizationContactName"
        fieldName="contactUserName"
        fieldValue={props.contactUserName.id}
        validator={requiredObjectValidator}
        required
        children={() => (
          <SuggestionBox
            defaultSelectedItem={props.contactUserName.id ? props.contactUserName : undefined}
            onSearchTextChange={handleSearchContactNameChange}
            searchTextChanged={clearContactNameFieldValue}
            selectedItemChanged={changeOrganizationContactName}
            placeholderKey="organization.EditOrganizationContactDetailsName.suggestion-placeholder"
            noResultsFoundKey="general.organization.contact.not-found"
            clearCurrentItem={() => clearContactNameFieldValue("")}
          />
        )}
        errorName="contactUserName"
      />
      <FormField
        id="contact-user-email"
        labelKey="general.email"
        fieldName="contactUserEmail"
        fieldValue={props.contactUserEmail.id}
        validator={requiredObjectValidator}
        required
        children={() => (
          <SuggestionBox
            defaultSelectedItem={props.contactUserEmail.id ? props.contactUserEmail : undefined}
            onSearchTextChange={(searchText) => handleSearchContactEmailChange(searchText)}
            searchTextChanged={clearEmailFieldValue}
            selectedItemChanged={changeOrganizationContactEmail}
            placeholderKey="organization.EditOrganizationContactDetailsEmail.suggestion-placeholder"
            noResultsFoundKey="general.organization.contact.not-found"
            clearCurrentItem={() => clearEmailFieldValue("")}
          />
        )}
        errorName="contactUserEmail"
      />
    </div>
  );
}
